'use client';

import { c as _c } from "react/compiler-runtime";
import { useDraw, useGetDrawResult } from '@svs/nova-poolbetting/core/nextjs/draw';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { EventNumber } from '@svs/nova-poolbetting/schema/sport';
import { getEventStatusForDraw, getIsNotStarted } from '@svs/nova-poolbetting/ui/Init';
import type { ReactNode } from 'react';
import { CouponError } from './CouponError';
import { CouponEventInit } from './CouponEventInit';
type CouponEventInitContainerProps = {
  product: Definition['drawProducts'][number];
  drawNumber: DrawNumber;
  eventNumber: EventNumber;
  className?: string;
};

/**
 * Container component for rendering the CouponEventInit component.
 *
 * @param root0 - The props for the CouponEventInitContainer component.
 * @param root0.product The product used for coupon
 * @param root0.drawNumber The draw number for the coupon
 * @param root0.eventNumber The event number for coupon event
 * @param root0.className Optional class name for styling
 * @returns A ReactNode that renders the CouponEventInit.
 */
export function CouponEventInitContainer(t0) {
  const $ = _c(28);
  const {
    product,
    drawNumber,
    eventNumber,
    className
  } = t0;
  let t1;
  if ($[0] !== drawNumber || $[1] !== product) {
    t1 = {
      product,
      drawNumber
    };
    $[0] = drawNumber;
    $[1] = product;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const drawData = useDraw(t1);
  let t2;
  if ($[3] !== drawNumber || $[4] !== product) {
    t2 = {
      product,
      drawNumber
    };
    $[3] = drawNumber;
    $[4] = product;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const drawResult = useGetDrawResult(t2);
  if (drawData.isError) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CouponError message="N/A" />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const {
    draw,
    bettingEvent
  } = drawData.data;
  const drawStatus = draw.drawState.client;
  const drawEvent = draw.events[eventNumber - 1];
  const eventBettingId = drawEvent?.status === "COMPLETE" ? drawEvent.bettingEventId : undefined;
  if (!eventBettingId) {
    return;
  }
  const betEvent = bettingEvent[eventBettingId];
  if (!betEvent) {
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CouponError message="N/A" />;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t3;
  if ($[8] !== betEvent.status) {
    t3 = getEventStatusForDraw(betEvent.status);
    $[8] = betEvent.status;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const matchDrawStatus = t3;
  if (drawStatus === "Opened" || drawResult.isError || drawResult.data.type === "NONE") {
    let t4;
    if ($[10] !== betEvent || $[11] !== className || $[12] !== draw.sportType || $[13] !== matchDrawStatus) {
      t4 = <CouponEventInit className={className} betEvent={betEvent} sportType={draw.sportType} matchDrawStatus={matchDrawStatus} />;
      $[10] = betEvent;
      $[11] = className;
      $[12] = draw.sportType;
      $[13] = matchDrawStatus;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  let t4;
  if ($[15] !== drawResult.data.data.drawResults || $[16] !== eventNumber) {
    let t5;
    if ($[18] !== eventNumber) {
      t5 = event => event.eventNumber === eventNumber;
      $[18] = eventNumber;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    t4 = drawResult.data.data.drawResults.find(t5);
    $[15] = drawResult.data.data.drawResults;
    $[16] = eventNumber;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const drawEventResult = t4;
  const hasMatchStarted = !getIsNotStarted(betEvent.status);
  const t5 = drawStatus === "Finished";
  let t6;
  if ($[20] !== betEvent || $[21] !== className || $[22] !== draw.sportType || $[23] !== drawEventResult || $[24] !== hasMatchStarted || $[25] !== matchDrawStatus || $[26] !== t5) {
    t6 = <CouponEventInit className={className} betEvent={betEvent} sportType={draw.sportType} drawEventResult={drawEventResult} matchDrawStatus={matchDrawStatus} hasMatchStarted={hasMatchStarted} isDrawFinished={t5} />;
    $[20] = betEvent;
    $[21] = className;
    $[22] = draw.sportType;
    $[23] = drawEventResult;
    $[24] = hasMatchStarted;
    $[25] = matchDrawStatus;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
}